//deps
import React, { memo, useContext, useMemo } from "react";

//internal-deps
import { TurboPayment, TurboPaymentProps, TurboPaymentVariant } from "@swiggy-private/payment-client";
import { useEffectOnce } from "@swiggy-private/core-ui";
// contexts
import { AnalyticsContext } from "@swiggy-private/checkout-core";

//helpers
import { canSendFlipkartInstalledEvent } from "@cart-client/helpers/CartFooter";

//analytics
import { impressionFlipkartInstalled } from "@cart-client/analytics/events";
import { CartContext } from "@cart-client/contexts/CartContext";
import { getGrandTotalFromRenderingDetails } from "@cart-client/helpers/Cart";

export interface TurboPaymentCartWrapperProps extends TurboPaymentProps {
    isTurboV2?: boolean;
    cartCallbackOnTurboPaymentChange?: VoidFunction;
    setForceExtraPaddingOnCart: (forceExtraPaddingOnCart: boolean) => void;
    setIsTurboPayWidgetHidden: (isTurboPayWidgetHidden: boolean) => void;
    billAmountShownOnCart?: string;
    onTurboStripImpressionEvent?: (isNewBanner: boolean, bannerId: string, stripMsg: string) => void;
    onTurboStripClickEvent?: (isNewBanner: boolean, bannerId: string, stripMsg: string) => void;
    onTooltipImpressionEvent?: (campaignId: string, stripMsg?: string) => void;
    onTooltipClickEvent?: (campaignId: string, stripMsg?: string) => void;
    onCvvPopupOpen?: VoidFunction;
    disableEduComms?: boolean;
    isQuickCheckout?: boolean;
}

function TurboPaymentCartWrapper({
    onError,
    onPaymentMethodChange,
    showLoader,
    uniqueId,
    hideEducationComms,
    showDiscountedValueOnSlider,
    isTurboV2 = false,
    cartCallbackOnTurboPaymentChange,
    setForceExtraPaddingOnCart,
    billAmountShownOnCart,
    setIsTurboPayWidgetHidden,
    onTurboStripImpressionEvent,
    onTurboStripClickEvent,
    onTooltipImpressionEvent,
    onTooltipClickEvent,
    onCvvPopupOpen,
    onTurboLoaded,
    hideTurboStrip,
    disableEduComms = false,
    isQuickCheckout = false,
    variant = TurboPaymentVariant.DEFAULT,
    variantData,
}: TurboPaymentCartWrapperProps) {
    const { impressionEvent } = useContext(AnalyticsContext);
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;

    const billAmountFromRenderingDetails = useMemo(
        () => getGrandTotalFromRenderingDetails(cartResponse),
        [cartResponse],
    );

    useEffectOnce(() => {
        // short lived code to send flipkart installed event to meet adhoc requirement from business
        // TODO: remove this code once campaign is over
        const sendFlipkartInstalledEvent = async () => {
            if (await canSendFlipkartInstalledEvent()) {
                impressionFlipkartInstalled(impressionEvent);
            }
        };
        sendFlipkartInstalledEvent();
    });

    return (
        <>
            <TurboPayment
                uniqueId={uniqueId}
                onPaymentMethodChange={onPaymentMethodChange}
                onError={onError}
                hideEducationComms={hideEducationComms}
                showLoader={showLoader}
                showDiscountedValueOnSlider={showDiscountedValueOnSlider}
                isTurboV2={isTurboV2}
                cartCallbackOnTurboPaymentChange={cartCallbackOnTurboPaymentChange}
                setForceExtraPaddingOnCart={setForceExtraPaddingOnCart}
                billAmountShownOnCart={billAmountShownOnCart}
                billAmountFromRenderingDetails={billAmountFromRenderingDetails}
                setIsTurboPayWidgetHidden={setIsTurboPayWidgetHidden}
                onTurboStripImpressionEvent={onTurboStripImpressionEvent}
                onTurboStripClickEvent={onTurboStripClickEvent}
                onTooltipImpressionEvent={onTooltipImpressionEvent}
                onTooltipClickEvent={onTooltipClickEvent}
                onCvvPopupOpen={onCvvPopupOpen}
                onTurboLoaded={onTurboLoaded}
                hideTurboStrip={hideTurboStrip}
                disableEduComms={disableEduComms}
                isQuickCheckout={isQuickCheckout}
                variant={variant}
                variantData={variantData}
            />
        </>
    );
}

export default memo(TurboPaymentCartWrapper);
